import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Check, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const colorPalette = [
  "#EC4899", "#EAB308", "#3B82F6", "#8B5CF6", "#10B981",
  "#F97316", "#06B6D4", "#22C55E", "#6366F1", "#F59E0B",
  "#EF4444", "#14B8A6", "#A855F7", "#84CC16", "#0EA5E9",
];

export default function SettingsDialog({ open, onClose, groups, statuses, onChanged }) {
  const [tab, setTab] = useState("groups");
  const items = tab === "groups" ? groups : statuses;
  const endpoint = tab === "groups" ? "/groups" : "/statuses";

  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(colorPalette[0]);
  const [editing, setEditing] = useState(null); // {id, name, color}

  const add = async () => {
    if (!newName.trim()) return;
    await api.post(endpoint, { name: newName.trim(), color: newColor });
    setNewName("");
    setNewColor(colorPalette[0]);
    onChanged?.();
    toast.success("Added");
  };

  const saveEdit = async () => {
    if (!editing.name.trim()) return;
    await api.put(`${endpoint}/${editing.id}`, { name: editing.name, color: editing.color });
    setEditing(null);
    onChanged?.();
    toast.success("Updated");
  };

  const remove = async (id) => {
    await api.delete(`${endpoint}/${id}`);
    onChanged?.();
    toast.success("Removed");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="!max-w-xl bg-white rounded-2xl border-slate-100 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="font-heading text-xl text-slate-900">Customize</DialogTitle>
        </DialogHeader>

        <div className="px-6 pt-4">
          <div className="inline-flex bg-slate-100 rounded-lg p-1">
            {["groups", "statuses"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-1.5 text-sm rounded-md font-heading capitalize transition-colors ${tab === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
                data-testid={`settings-tab-${t}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
          <div className="space-y-2 mb-5">
            {items.map((g) => (
              <div key={g.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-100 bg-slate-50">
                {editing?.id === g.id ? (
                  <>
                    <ColorDot color={editing.color} />
                    <input
                      className="mr-input flex-1 !py-1"
                      value={editing.name}
                      onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    />
                    <ColorPicker value={editing.color} onChange={(c) => setEditing({ ...editing, color: c })} />
                    <button onClick={saveEdit} className="text-emerald-600 hover:text-emerald-700">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditing(null)} className="text-slate-400">
                      <X className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <ColorDot color={g.color} />
                    <span className="flex-1 text-sm text-slate-700 font-medium">{g.name}</span>
                    <button onClick={() => setEditing({ id: g.id, name: g.name, color: g.color })} className="text-slate-400 hover:text-slate-700" data-testid={`edit-${tab}-${g.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(g.id)} className="text-slate-400 hover:text-rose-500" data-testid={`delete-${tab}-${g.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="border-t border-slate-100 pt-4">
            <div className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-500 mb-2">
              Add new {tab === "groups" ? "group" : "status"}
            </div>
            <div className="flex items-center gap-2">
              <ColorPicker value={newColor} onChange={setNewColor} />
              <input
                className="mr-input flex-1"
                placeholder="Name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && add()}
                data-testid={`new-${tab}-name-input`}
              />
              <button onClick={add} className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium font-heading hover:bg-slate-800" data-testid={`add-${tab}-button`}>
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium font-heading hover:bg-slate-800">
            Done
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ColorDot({ color }) {
  return <span className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: color }} />;
}

function ColorPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center bg-white">
        <span className="w-4 h-4 rounded-full" style={{ backgroundColor: value }} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 bg-white border border-slate-200 rounded-lg p-2 shadow-lg grid grid-cols-5 gap-1.5 w-44">
          {colorPalette.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                onChange(c);
                setOpen(false);
              }}
              className="w-6 h-6 rounded-full border-2 border-white outline outline-1 outline-slate-100 hover:scale-110 transition"
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
